import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../../api/axios';
import { useCart } from '../../../context/CartContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import { FaStar, FaShoppingCart, FaHeart, FaShare, FaUserMd, FaStethoscope } from 'react-icons/fa';

export default function MedicineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [medicine, setMedicine] = useState(null);
  const [relatedMedicines, setRelatedMedicines] = useState([]);
  const [suggestedDoctors, setSuggestedDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const {
    cart,
    addMedicine,
    isAuthenticated
  } = useCart();

  // Handle add to cart with new backend integration
  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      await addMedicine(parseInt(id), 1);
      alert('Added to cart successfully!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart');
    }
  };

  // Get initials from medicine name
  const getInitials = (name) => {
    if (!name) return 'M';
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Medicine icon component for fallback - tablet style with medical elements
  const MedicineIcon = ({ className = "", medicineName = "" }) => {
    const initials = getInitials(medicineName);
    const primaryColor = '#10495e';
    const lightColor = '#e0f2f1';

    return (
      <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-2 relative rounded-xl ${className}`}>
        {/* Medical Cross - Top Left */}
        <div className="absolute top-2 left-2 opacity-20">
          <div className="relative w-3 h-3">
            <div className="absolute top-1/2 left-0 w-full h-0.5 transform -translate-y-1/2" style={{ backgroundColor: primaryColor }} />
            <div className="absolute top-0 left-1/2 w-0.5 h-full transform -translate-x-1/2" style={{ backgroundColor: primaryColor }} />
          </div>
        </div>

        {/* Droplet - Top Right */}
        <div className="absolute top-1 right-2 opacity-20">
          <svg width="10" height="12" viewBox="0 0 12 16" fill={primaryColor}>
            <path d="M6 0C6 0 0 6 0 10C0 13.3137 2.68629 16 6 16C9.31371 16 12 13.3137 12 10C12 6 6 0 6 0Z" />
          </svg>
        </div>

        {/* Main Tablet */}
        <div className="relative">
          {/* Tablet Shape */}
          <div
            className="rounded-lg px-4 py-3 md:px-5 md:py-4 shadow-lg flex items-center justify-center border-2 relative overflow-hidden"
            style={{
              background: `linear-gradient(145deg, ${primaryColor} 0%, #0d3a4d 100%)`,
              borderColor: primaryColor,
              borderWidth: '2px',
              borderStyle: 'solid',
            }}
          >
            {/* Dividing line (tablet score line) */}
            <div
              className="absolute top-0 left-1/2 w-px h-full transform -translate-x-1/2 opacity-40"
              style={{ backgroundColor: '#fff' }}
            />

            {/* Shine effect */}
            <div
              className="absolute top-0 left-0 w-1/3 h-full opacity-20"
              style={{
                background: 'linear-gradient(120deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
              }}
            />

            {/* Initials */}
            <span className="text-white text-sm md:text-base font-bold tracking-widest relative z-10">
              {initials}
            </span>
          </div>

          {/* Small round pill - bottom left */}
          <div
            className="absolute -bottom-0.5 -left-1.5 w-2.5 h-2.5 rounded-full shadow-md border"
            style={{
              backgroundColor: lightColor,
              borderColor: primaryColor,
              borderWidth: '1px'
            }}
          />

          {/* Small capsule - top right */}
          <div
            className="absolute -top-0.5 -right-1.5 rounded-full shadow-md border"
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              borderWidth: '1px',
              width: '12px',
              height: '6px'
            }}
          />
        </div>

        {/* Syringe Icon - Bottom Right */}
        <div className="absolute bottom-1.5 right-1.5 opacity-15">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2">
            <path d="M18 2l4 4M7.5 12.5l-5 5a2 2 0 0 0 0 2.828l.707.707a2 2 0 0 0 2.828 0l5-5M12 7l5 5" />
          </svg>
        </div>

        {/* Pills scattered - Bottom Left */}
        <div className="absolute bottom-1 left-1.5 opacity-20 flex gap-0.5">
          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }} />
          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }} />
        </div>
      </div>
    );
  };

  // Find quantity in cart (from backend cart)
  const getMedicineQuantityInCart = () => {
    if (!cart || !cart.medicine_items) return 0;
    const cartItem = cart.medicine_items.find(item => item.medicine?.id === parseInt(id));
    return cartItem ? cartItem.quantity : 0;
  };

  const quantity = getMedicineQuantityInCart();

  // Function to handle image error
  const handleImageError = () => {
    setImageError(true);
  };

  // Function to determine if we should show fallback
  const shouldShowFallback = (imageUrl) => {
    return !imageUrl || imageError;
  };

  // Fetch medicine data
  useEffect(() => {
    const fetchMedicine = async () => {
      try {
        const res = await api.get(`/medicines/${id}/`);
        if (res.data.success) {
          setMedicine(res.data.data);
          setImageError(false); // Reset image error when new medicine is loaded
        } else {
          alert('Medicine not found');
          navigate('/Medicines');
        }
      } catch (err) {
        console.error('Failed to load medicine details:', err);
        alert('Failed to load medicine details');
        navigate('/Medicines');
      } finally {
        setLoading(false);
      }
    };
    fetchMedicine();
  }, [id, navigate]);

  // Fetch related medicines
  useEffect(() => {
    const fetchRelatedMedicines = async () => {
      try {
        setRelatedLoading(true);
        const res = await api.get(`/suggestions/related-medicines/${id}/`);

        if (res.data.success) {
          setRelatedMedicines(res.data.related_medicines || []);
        } else {
          setRelatedMedicines([]);
        }
      } catch (err) {
        console.error('Failed to load related medicines:', err);
        setRelatedMedicines([]);
      } finally {
        setRelatedLoading(false);
      }
    };

    if (medicine) {
      fetchRelatedMedicines();
    }
  }, [medicine, id]);

  // Fetch suggested doctors
  useEffect(() => {
    const fetchSuggestedDoctors = async () => {
      try {
        setDoctorsLoading(true);
        const res = await api.get(`/suggestions/doctors-for-medicine/${id}/`);

        if (res.data.success) {
          setSuggestedDoctors(res.data.suggested_doctors || []);
        } else {
          setSuggestedDoctors([]);
        }
      } catch (err) {
        console.error('Failed to load suggested doctors:', err);
        setSuggestedDoctors([]);
      } finally {
        setDoctorsLoading(false);
      }
    };

    if (medicine) {
      fetchSuggestedDoctors();
    }
  }, [medicine, id]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex flex-col items-center justify-center min-h-96 text-gray-600 px-4">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-4 rounded-full animate-spin mb-4" style={{ borderTopColor: '#104a5f' }}></div>
          <p>Loading medicine details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!medicine) {
    return (
      <>
        <Header />
        <div className="text-center py-16 px-5 text-gray-600">
          <h2 className="text-xl font-semibold mb-4">Medicine not found</h2>
          <button 
            onClick={() => navigate('/medicines')} 
            className="px-5 py-2.5 text-white border-none rounded-md cursor-pointer mt-4 hover:opacity-90"
            style={{ backgroundColor: '#104a5f' }}
          >
            Back to Medicines
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const discount = Math.round(
    ((medicine.mrp - medicine.offer_price) / medicine.mrp) * 100
  );

  const SuggestionCard = ({ med }) => {
    const [suggestionImageError, setSuggestionImageError] = useState(false);

    // Get cart quantity for this medicine (from backend cart)
    const getQtyInCart = () => {
      if (!cart || !cart.medicine_items) return 0;
      const cartItem = cart.medicine_items.find(item => item.medicine?.id === med.id);
      return cartItem ? cartItem.quantity : 0;
    };
    const qty = getQtyInCart();

    // Handle add to cart for suggestion card
    const handleSuggestionAddToCart = async (e) => {
      e.stopPropagation();
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }
      try {
        await addMedicine(med.id, 1);
        alert('Added to cart successfully!');
      } catch (error) {
        console.error('Error adding to cart:', error);
        alert(error.message || 'Failed to add to cart');
      }
    };

    // Handle both price and mrp fields from different API responses
    const price = med.price || med.mrp || 0;
    const offerPrice = med.offer_price || med.price || 0;

    const suggestionDiscount = price && offerPrice ? Math.round(
      ((price - offerPrice) / price) * 100
    ) : 0;

    const shouldShowSuggestionFallback = (imageUrl) => {
      return !imageUrl || suggestionImageError;
    };

    // Small medicine icon for suggestions - tablet style
    const SmallMedicineIcon = () => {
      const initials = getInitials(med.name);
      const primaryColor = '#10495e';
      const lightColor = '#e0f2f1';

      return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-1.5 relative">
          {/* Medical Cross - Top Left */}
          <div className="absolute top-1 left-1 opacity-15">
            <div className="relative w-2 h-2">
              <div className="absolute top-1/2 left-0 w-full h-px transform -translate-y-1/2" style={{ backgroundColor: primaryColor }} />
              <div className="absolute top-0 left-1/2 w-px h-full transform -translate-x-1/2" style={{ backgroundColor: primaryColor }} />
            </div>
          </div>

          {/* Droplet - Top Right */}
          <div className="absolute top-0.5 right-1 opacity-15">
            <svg width="6" height="8" viewBox="0 0 12 16" fill={primaryColor}>
              <path d="M6 0C6 0 0 6 0 10C0 13.3137 2.68629 16 6 16C9.31371 16 12 13.3137 12 10C12 6 6 0 6 0Z" />
            </svg>
          </div>

          {/* Main Tablet */}
          <div className="relative">
            <div
              className="rounded px-3 py-2 shadow-md flex items-center justify-center border relative overflow-hidden"
              style={{
                background: `linear-gradient(145deg, ${primaryColor} 0%, #0d3a4d 100%)`,
                borderColor: primaryColor,
                borderWidth: '1.5px',
              }}
            >
              {/* Dividing line */}
              <div
                className="absolute top-0 left-1/2 w-px h-full transform -translate-x-1/2 opacity-30"
                style={{ backgroundColor: '#fff' }}
              />

              {/* Shine */}
              <div
                className="absolute top-0 left-0 w-1/3 h-full opacity-15"
                style={{
                  background: 'linear-gradient(120deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
                }}
              />

              {/* Initials */}
              <span className="text-white text-xs font-bold tracking-wide relative z-10">
                {initials}
              </span>
            </div>

            {/* Small pill - bottom left */}
            <div
              className="absolute -bottom-0.5 -left-1 w-1.5 h-1.5 rounded-full shadow-sm border"
              style={{
                backgroundColor: lightColor,
                borderColor: primaryColor,
                borderWidth: '0.5px'
              }}
            />

            {/* Small capsule - top right */}
            <div
              className="absolute -top-0.5 -right-1 rounded-full shadow-sm border"
              style={{
                backgroundColor: primaryColor,
                width: '8px',
                height: '4px'
              }}
            />
          </div>

          {/* Pills scattered */}
          <div className="absolute bottom-0.5 left-1 opacity-15 flex gap-px">
            <div className="w-0.5 h-0.5 rounded-full" style={{ backgroundColor: primaryColor }} />
            <div className="w-0.5 h-0.5 rounded-full" style={{ backgroundColor: primaryColor }} />
          </div>
        </div>
      );
    };

    return (
      <div 
        className="bg-white rounded-xl overflow-hidden shadow-md cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105"
        onClick={() => navigate(`/medicine/${med.id}`)}
      >
        <div className="relative h-35 overflow-hidden">
          {shouldShowSuggestionFallback(med.image) ? (
            <SmallMedicineIcon />
          ) : (
            <img
              src={med.image}
              alt={med.name}
              className="w-full h-full object-cover"
              onError={() => setSuggestionImageError(true)}
              onLoad={() => setSuggestionImageError(false)}
            />
          )}
          {suggestionDiscount > 0 && (
            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
              {suggestionDiscount}% OFF
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h4 className="text-sm font-bold text-gray-800 mb-1 leading-tight line-clamp-2">{med.name}</h4>
          {med.category && (
            <p className="text-xs text-teal-600 mb-2 font-medium">{med.category}</p>
          )}

          <div className="flex items-center gap-2 mb-3">
            <span className="text-base font-bold text-gray-800">₹{offerPrice}</span>
            {price > offerPrice && (
              <span className="text-xs text-gray-500 line-through">₹{price}</span>
            )}
          </div>

          {med.is_prescription_required && (
            <div className="mb-2 text-xs text-orange-600 flex items-center gap-1">
              <span>⚕️</span>
              <span>Prescription Required</span>
            </div>
          )}

          {(med.is_available !== false && med.in_stock !== false) ? (
            qty > 0 ? (
              <div className="flex flex-col gap-1.5">
                <div className="text-xs text-green-600 font-semibold text-center">
                  {qty} in cart
                </div>
                <Link
                  to="/cart"
                  onClick={(e) => e.stopPropagation()}
                  className="w-full bg-green-600 text-white text-xs py-1.5 rounded hover:bg-green-700 transition-colors font-medium text-center no-underline"
                >
                  View Cart
                </Link>
              </div>
            ) : (
              <button
                onClick={handleSuggestionAddToCart}
                className="w-full flex items-center justify-center gap-1 py-2 px-3 text-xs font-bold text-white rounded-md transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#104a5f' }}
              >
                <FaShoppingCart style={{ marginRight: '4px' }} />
                Add
              </button>
            )
          ) : (
            <div className="w-full text-center py-2 text-xs font-semibold text-red-600">
              Out of Stock
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="p-4 md:p-5 max-w-7xl mx-auto bg-gray-50 min-h-screen">
        {/* Breadcrumb */}
        <div className="mb-5 text-sm text-gray-600">
          <span onClick={() => navigate('/')} className="text-blue-600 cursor-pointer hover:underline">Home</span>
          <span className="mx-2 text-gray-400">/</span>
          <span onClick={() => navigate('/medicines')} className="text-blue-600 cursor-pointer hover:underline">Medicines</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-800 font-medium">{medicine.name}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 mb-15 bg-white rounded-xl p-6 md:p-8 shadow-lg">
          {/* Image */}
          <div className="w-full lg:w-96 lg:flex-shrink-0">
            <div className="w-full max-w-md mx-auto lg:max-w-none">
              {shouldShowFallback(medicine.image) ? (
                <MedicineIcon medicineName={medicine.name} />
              ) : (
                <img
                  src={medicine.image}
                  alt={medicine.name}
                  className="w-full rounded-xl shadow-lg"
                  onError={handleImageError}
                  onLoad={() => setImageError(false)}
                />
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 leading-tight">{medicine.name}</h1>

            <div className="mb-6">
              {medicine.manufacturer && (
                <p className="mb-2 text-base" style={{ color: '#104a5f' }}>
                  <strong>Manufacturer:</strong> {medicine.manufacturer}
                </p>
              )}
              {medicine.pack_size && (
                <p className="text-gray-600 mb-3 text-sm">{medicine.pack_size}</p>
              )}
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(star => (
                    <FaStar key={star} className="text-yellow-500 text-base" />
                  ))}
                </div>
                <span className="text-gray-600 text-sm">
                  {medicine.average_rating || '4.5'} ({medicine.total_reviews || 0} reviews)
                </span>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl md:text-4xl font-bold text-gray-800">₹{medicine.offer_price}</span>
                {medicine.mrp > medicine.offer_price && (
                  <span className="text-lg text-gray-500 line-through">MRP ₹{medicine.mrp}</span>
                )}
              </div>
              {discount > 0 && (
                <div className="text-green-500 font-bold text-lg">{discount}% OFF</div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              {quantity > 0 ? (
                <div className="flex items-center gap-3 bg-gray-100 p-2 rounded-lg">
                  <span className="text-green-600 font-semibold mr-2">{quantity} in cart</span>
                  <button
                    onClick={() => navigate('/cart')}
                    className="text-white border-none py-2 px-4 rounded-md text-sm cursor-pointer font-bold hover:opacity-90"
                    style={{ backgroundColor: '#104a5f' }}
                  >
                    View Cart
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="flex items-center gap-2 text-white py-3.5 px-7 rounded-lg border-none cursor-pointer font-bold text-base transition-all duration-300 hover:opacity-90 hover:scale-105"
                  style={{ backgroundColor: '#104a5f' }}
                >
                  <FaShoppingCart />
                  Add to Cart
                </button>
              )}
              
              <div className="flex gap-2">
                <button className="bg-gray-100 border border-gray-300 rounded-lg p-3 cursor-pointer text-gray-600 hover:bg-gray-200 transition-all duration-300">
                  <FaHeart />
                </button>
                <button className="bg-gray-100 border border-gray-300 rounded-lg p-3 cursor-pointer text-gray-600 hover:bg-gray-200 transition-all duration-300">
                  <FaShare />
                </button>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 p-5 bg-gray-100 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="text-base">✅</span>
                <span>100% Genuine Medicines</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="text-base">🔐</span>
                <span>Secure Payments</span>
              </div>
            </div>

            {/* Description */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Product Description</h3>
              <div className="text-gray-600 leading-relaxed text-sm md:text-base whitespace-pre-line">
                {medicine.description}
              </div>
            </div>
          </div>
        </div>

        {/* Suggested Doctors Section */}
        {suggestedDoctors.length > 0 && (
          <div className="mt-10 bg-white rounded-xl p-6 md:p-8 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                  <FaUserMd className="text-teal-600" />
                  Consult with Specialists
                </h2>
                <p className="text-gray-600 text-sm mt-2">Expert doctors for this medicine</p>
              </div>
            </div>

            {doctorsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-gray-50 rounded-xl p-6 animate-pulse">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gray-300 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-300 rounded mb-2"></div>
                        <div className="h-4 bg-gray-300 rounded w-2/3 mb-2"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestedDoctors.slice(0, 6).map(doctor => (
                  <div
                    key={doctor.id}
                    className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-100 hover:border-teal-300 transition-all duration-300 hover:shadow-lg cursor-pointer"
                    onClick={() => navigate(`/doctor/${doctor.id}`)}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-2 border-teal-500 flex-shrink-0">
                        {doctor.profile_image ? (
                          <img
                            src={`https://www.homecureexpress.com${doctor.profile_image}`}
                            alt={doctor.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center text-3xl ${doctor.profile_image ? 'hidden' : 'flex'}`}>
                          👨‍⚕️
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 text-lg mb-1">{doctor.name}</h3>
                        <p className="text-teal-600 font-semibold text-sm mb-1">{doctor.specialization}</p>
                        <div className="flex items-center gap-2">
                          {parseFloat(doctor.average_rating) > 0 && (
                            <>
                              <div className="flex items-center gap-1">
                                <FaStar className="text-yellow-500 text-xs" />
                                <span className="text-sm font-semibold text-gray-700">{doctor.average_rating}</span>
                              </div>
                              <span className="text-gray-400">•</span>
                            </>
                          )}
                          {doctor.experience_years > 0 && (
                            <span className="text-sm text-gray-600">{doctor.experience_years} years exp</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {doctor.bio && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{doctor.bio}</p>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-teal-200">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Consultation Fee</p>
                        <p className="text-xl font-bold text-teal-600">₹{doctor.price}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/doctor/${doctor.id}`);
                        }}
                        className="bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors flex items-center gap-2"
                      >
                        <FaStethoscope />
                        Consult Now
                      </button>
                    </div>

                    {doctor.is_available_now && (
                      <div className="flex items-center gap-2 mt-3 text-green-600 text-xs font-semibold">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Available Now
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Related Medicines Section */}
        <div className="mt-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">Related Medicines</h2>

          {relatedLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-md">
                  <div className="w-full h-30 bg-gray-200 rounded-lg mb-3 animate-pulse"></div>
                  <div className="w-4/5 h-4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="w-3/5 h-5 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : relatedMedicines.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 mb-8">
                {relatedMedicines.map(med => (
                  <SuggestionCard key={med.id} med={med} />
                ))}
              </div>
              <div className="text-center">
                <button
                  onClick={() => navigate('/medicines')}
                  className="bg-transparent border-2 rounded-lg py-3 px-6 text-base font-bold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-md"
                  style={{ color: '#104a5f', borderColor: '#104a5f' }}
                >
                  View All Medicines
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No related medicines found</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

